// Must wrap indexedDB.open before any Firebase SDK opens a database.
import './idbDiagnostics'
import { initializeApp } from 'firebase/app'
import { Capacitor } from '@capacitor/core'
import { log } from './logger'
import { createEnsureAnonAuth } from './anonAuth'
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  signInAnonymously,
  connectAuthEmulator,
  onAuthStateChanged,
} from 'firebase/auth'
import {
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
  memoryLocalCache,
  connectFirestoreEmulator,
  collection,
  query,
  limit,
  getDocsFromServer,
} from 'firebase/firestore'
import { getStorage, connectStorageEmulator } from 'firebase/storage'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics'
import { getPerformance, type FirebasePerformance } from 'firebase/performance'

const useEmulator = import.meta.env.VITE_USE_EMULATOR === 'true'

// In emulator mode (E2E) use a fixed demo config so the suite needs no real
// Firebase credentials in CI — only the project id matters and it must match
// the emulator project. Otherwise read the real project config from env.
const firebaseConfig = useEmulator
  ? {
      apiKey: 'demo-api-key',
      authDomain: 'localhost',
      projectId: 'beacon-festival-app',
      storageBucket: 'demo.appspot.com',
      messagingSenderId: '000000000000',
      appId: '1:000000000000:web:demo',
    }
  : {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    }

const app = initializeApp(firebaseConfig)

// On iOS, IndexedDB hangs in WKWebView's capacitor:// scheme, blocking both
// Auth (persistence init) and Firestore (persistent cache). Use localStorage
// for auth persistence on iOS to avoid the hang. Android's WebView uses
// https://localhost so IndexedDB works fine there.
const isIOS = Capacitor.getPlatform() === 'ios'
// After an OTA bundle swap, WKWebView's indexedDB.open() can hang forever
// (WebKit bug https://webkit.org/b/226547), blocking Firestore's persistent
// cache and causing infinite loading. The flag is set by ota.ts when a new
// bundle is queued; on the next boot we skip IndexedDB entirely and use an
// in-memory cache. The persistent cache resumes on the next cold start once
// the stale WebKit IDB state is cleared by a fresh process.
export const isOtaBoot = isIOS && !!localStorage.getItem('ota_pending_reload')
if (isOtaBoot) localStorage.removeItem('ota_pending_reload')
export const firestoreCacheMode: 'persistent' | 'memory' = isOtaBoot ? 'memory' : 'persistent'

// Firebase Auth validates the persisted user over the network
// (accounts:lookup, preceded by a token refresh when expired) BEFORE emitting
// the first auth state, and Firestore queues every operation — persistent-
// cache reads included — behind that first emission. On a slow-but-alive
// connection those requests hang for 10-30s and the whole app skeletons
// despite a warm cache; in airplane mode they fail instantly and everything
// renders from cache right away (issue #298). Give the init-time auth
// requests a hard budget: an aborted fetch maps to auth/network-request-failed,
// which makes Auth proceed with the persisted user — the airplane-mode path.
// The interceptor uninstalls itself after the first auth state resolves, so
// sign-in flows and background token refreshes are never affected.
const AUTH_INIT_FETCH_TIMEOUT_MS = 1_000
let restoreFetch: (() => void) | null = null
if (!useEmulator) {
  const originalFetch = globalThis.fetch.bind(globalThis)
  const isAuthRequest = (input: RequestInfo | URL): boolean => {
    const url =
      typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    return (
      url.includes('identitytoolkit.googleapis.com') ||
      url.includes('securetoken.googleapis.com')
    )
  }
  globalThis.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (!isAuthRequest(input)) return originalFetch(input, init)
    const controller = new AbortController()
    const timer = setTimeout(() => {
      log('auth', 'init request aborted — proceeding with persisted user', {
        timeout_ms: AUTH_INIT_FETCH_TIMEOUT_MS,
      })
      controller.abort()
    }, AUTH_INIT_FETCH_TIMEOUT_MS)
    return originalFetch(input, { ...init, signal: controller.signal }).finally(() =>
      clearTimeout(timer),
    )
  }) as typeof fetch
  restoreFetch = () => {
    globalThis.fetch = originalFetch
    restoreFetch = null
  }
}

export const auth = isIOS
  ? initializeAuth(app, { persistence: browserLocalPersistence })
  : getAuth(app)

// First auth emission = init finished; put the untouched fetch back.
if (restoreFetch) {
  const unsubscribe = onAuthStateChanged(auth, () => {
    restoreFetch?.()
    unsubscribe()
  })
}
export const storage = getStorage(app)
export const functions = getFunctions(app)

// Safari 26.4+ and all iOS browsers (Chrome/Firefox on iOS also use WebKit) have
// a confirmed bug where Firestore's FetchStreams transport silently hangs for
// 10-30s. Fall back to XHR/WebChannel on any WebKit-based browser.
// https://github.com/firebase/firebase-js-sdk/issues/9789
const isWebKit =
  Capacitor.getPlatform() === 'ios' ||
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (/^((?!chrome|android).)*safari/i.test(navigator.userAgent))
export const db = initializeFirestore(app, {
  localCache: isOtaBoot
    ? memoryLocalCache()
    : persistentLocalCache({
        ...(isIOS ? { tabManager: persistentSingleTabManager({ forceOwnership: true }) } : {}),
      }),
  ...(isWebKit ? { experimentalForceLongPolling: true, useFetchStreams: false } : {}),
})
log('fs', isOtaBoot ? 'init — memory cache (OTA boot)' : 'init — persistent cache', { webkit_workaround: isWebKit, isIOS, singleTab: isIOS && !isOtaBoot })

// E2E only: point Auth + Firestore at the local emulators. Gated on an explicit
// flag so dev, prod, and the --verify smoke flow keep hitting real Firebase.
// Must run before the warm-up read below so it never reaches a live backend.
if (useEmulator) {
  // Each E2E/showcase run gets its own emulator ports (scripts/emulator-run.ts).
  const port = (value: string | undefined, fallback: number) => Number(value) || fallback
  const env = import.meta.env
  connectAuthEmulator(auth, `http://localhost:${port(env.VITE_EMULATOR_AUTH_PORT, 9099)}`, { disableWarnings: true })
  connectFirestoreEmulator(db, 'localhost', port(env.VITE_EMULATOR_FIRESTORE_PORT, 8080))
  connectStorageEmulator(storage, 'localhost', port(env.VITE_EMULATOR_STORAGE_PORT, 9199))
  connectFunctionsEmulator(functions, 'localhost', port(env.VITE_EMULATOR_FUNCTIONS_PORT, 5001))
}

// On native, analytics goes through @capacitor-firebase/analytics (native SDK);
// on web, we init the JS SDK here as before.
export let analytics: Analytics | null = null
// isSupported() resolves a tick or more after module load, so anything tracked
// during the app's first render would hit a null `analytics`. analytics.ts
// queues on this promise rather than dropping those events.
export const analyticsReady: Promise<void> =
  import.meta.env.PROD && firebaseConfig.measurementId && !Capacitor.isNativePlatform()
    ? isSupported().then(ok => {
        if (ok) analytics = getAnalytics(app)
      })
    : Promise.resolve()

export let perf: FirebasePerformance | null = null
if (import.meta.env.PROD) {
  perf = getPerformance(app)
}


// Fire one tiny public read at module load so the WebChannel handshake happens
// in parallel with auth resolution and bundle parse. Skip when offline — no
// point warming a dead connection, and getDocsFromServer hangs without network.
if (navigator.onLine) {
  const warmupStart = performance.now()
  log('fs','warmup read started')
  void getDocsFromServer(query(collection(db, 'events'), limit(1)))
    .then(() => log('fs','warmup read done', { elapsed_ms: Math.round(performance.now() - warmupStart) }))
    .catch((err: unknown) => log('fs','warmup read failed', { error: String(err) }))
} else {
  log('fs','warmup read skipped — offline')
}

export const ensureAnonAuth = createEnsureAnonAuth(auth, () => signInAnonymously(auth))
